/**
 * Constant-acceleration Kalman filter with 2D observation.
 * State: [x, y, vx, vy, ax, ay] (6D)
 * Observation: [x, y] (2D)
 *
 * Minimal hand-rolled linear algebra is fine given the fixed 6×6/2×2 sizes.
 */

const STATE_DIM = 6;

function mulMat(a: number[][], b: number[][]): number[][] {
  const rows = a.length;
  const cols = b[0].length;
  const inner = b.length;
  const out: number[][] = Array.from({ length: rows }, () => Array.from({ length: cols }, () => 0));
  for (let i = 0; i < rows; i += 1) {
    for (let k = 0; k < inner; k += 1) {
      const v = a[i][k];
      if (v === 0) continue;
      for (let j = 0; j < cols; j += 1) out[i][j] += v * b[k][j];
    }
  }
  return out;
}

function transpose(a: number[][]): number[][] {
  const rows = a.length;
  const cols = a[0].length;
  const out: number[][] = Array.from({ length: cols }, () => Array.from({ length: rows }, () => 0));
  for (let i = 0; i < rows; i += 1) {
    for (let j = 0; j < cols; j += 1) out[j][i] = a[i][j];
  }
  return out;
}

function addMat(a: number[][], b: number[][]): number[][] {
  return a.map((row, i) => row.map((v, j) => v + b[i][j]));
}

function subMat(a: number[][], b: number[][]): number[][] {
  return a.map((row, i) => row.map((v, j) => v - b[i][j]));
}

function scaleMat(a: number[][], s: number): number[][] {
  return a.map((row) => row.map((v) => v * s));
}

function identity(n: number): number[][] {
  const out = Array.from({ length: n }, () => Array.from({ length: n }, () => 0));
  for (let i = 0; i < n; i += 1) out[i][i] = 1;
  return out;
}

function invert2x2(m: number[][]): number[][] | null {
  const [[a, b], [c, d]] = m;
  const det = a * d - b * c;
  if (Math.abs(det) < 1e-12) return null;
  const inv = 1 / det;
  return [
    [d * inv, -b * inv],
    [-c * inv, a * inv],
  ];
}

export interface KalmanOptions {
  processNoise?: number;
  observationNoise?: number;
  initialCovariance?: number;
}

export class KalmanFilter2D {
  private state: number[][];
  private P: number[][];
  private readonly processNoise: number;
  private readonly observationNoise: number;

  constructor(x0: number, y0: number, opts: KalmanOptions = {}) {
    this.processNoise = opts.processNoise ?? 1e-3;
    this.observationNoise = opts.observationNoise ?? 1e-2;
    const p0 = opts.initialCovariance ?? 1;
    this.state = [[x0], [y0], [0], [0], [0], [0]];
    this.P = identity(STATE_DIM).map((row) => row.map((v) => v * p0));
  }

  reset(x0: number, y0: number): void {
    this.state = [[x0], [y0], [0], [0], [0], [0]];
    this.P = identity(STATE_DIM);
  }

  /** Predict state forward by dt seconds. */
  predict(dtSec: number): void {
    const dt = Math.max(0, dtSec);
    const dt2 = dt * dt * 0.5;
    const F: number[][] = [
      [1, 0, dt, 0, dt2, 0],
      [0, 1, 0, dt, 0, dt2],
      [0, 0, 1, 0, dt, 0],
      [0, 0, 0, 1, 0, dt],
      [0, 0, 0, 0, 1, 0],
      [0, 0, 0, 0, 0, 1],
    ];
    this.state = mulMat(F, this.state);
    const Ft = transpose(F);
    const Q = scaleMat(identity(STATE_DIM), this.processNoise);
    this.P = addMat(mulMat(mulMat(F, this.P), Ft), Q);
  }

  /** Fold in a new measurement (zx, zy). */
  update(zx: number, zy: number): void {
    const H: number[][] = [
      [1, 0, 0, 0, 0, 0],
      [0, 1, 0, 0, 0, 0],
    ];
    const Ht = transpose(H);
    const R: number[][] = [
      [this.observationNoise, 0],
      [0, this.observationNoise],
    ];

    const z: number[][] = [[zx], [zy]];
    const yResid = subMat(z, mulMat(H, this.state));
    const S = addMat(mulMat(mulMat(H, this.P), Ht), R);
    const Sinv = invert2x2(S);
    if (!Sinv) return;
    const K = mulMat(mulMat(this.P, Ht), Sinv);
    this.state = addMat(this.state, mulMat(K, yResid));
    const I = identity(STATE_DIM);
    this.P = mulMat(subMat(I, mulMat(K, H)), this.P);
  }

  position(): { x: number; y: number } {
    return { x: this.state[0][0], y: this.state[1][0] };
  }

  velocity(): { x: number; y: number } {
    return { x: this.state[2][0], y: this.state[3][0] };
  }

  acceleration(): { x: number; y: number } {
    return { x: this.state[4][0], y: this.state[5][0] };
  }
}
